import { createSelector } from 'reselect';

import _isEmpty from 'lodash-es/isEmpty';
import _values from 'lodash-es/values';

import * as ImagesClient from 'Clients/Images';

import RootState from 'Store/Root';
import { DesignState } from 'Store/Designs/Reducer';
import { RoomsState } from 'Store/Rooms/Reducer';
import { getValidRugRooms } from 'Store/Rooms/Utils';

import {
  AvailableItemOptions,
  BasketItemDisplay,
  BasketItemRug,
  RugState,
  RoomSceneOption,
  TrykProductOption
} from 'Types/Basket';

import { filterRugProducts } from 'Utils/Tryk';

import {
  BasicItemOptions,
  basicOptionsSelector
} from './BasicOptions';

const rugStatesSelector = (state: RootState): RugState[] => {
  const {
    rugsById,
    itemsList
  } = state.basket;

  return itemsList.map(x => rugsById[x])
    .filter(x => !_isEmpty(x));
};

export const hasRugsLoadedSelector = createSelector(
  rugStatesSelector,
  (state: RootState) => state.designs.itemsByCode,
  (items: RugState[], designs: _.Dictionary<DesignState>): boolean => {
    return items.every(
      x => x.rug.components.every(
        y => !!designs[y.designCode.toUpperCase()]
      )
    );
  }
);

export const rugItemsSelector = createSelector(
  hasRugsLoadedSelector,
  rugStatesSelector,
  basicOptionsSelector,
  (state: RootState) => _values(state.trykLayouts.itemsById),
  (state: RootState) => state.rooms,
  (hasLoaded: boolean, items: RugState[], options: BasicItemOptions, products: TrykApi.Catalog.ITrykProduct[], rooms: RoomsState): BasketItemRug[] =>
    (hasLoaded ? items : []).map((item): BasketItemRug => ({
      id: item.id,
      rug: item.rug,
      display: getBasketRugDisplay(item.rug),
      printScale: options.printScales.find(x => x.value === item.printScale),
      brand: options.brands.find(x => x.brandId === item.layoutBrandId) || options.brands[0],
      product: getTrykProductOption(products.find(x => x.productId === item.productId)),
      room: getRugRooms(rooms, item.rug).find(
        x => x.roomId === item.roomId
      )
    })
    )
);

export function getAvailableOptions(item: BasketItemRug, state: RootState): AvailableItemOptions {
  const options = basicOptionsSelector(state);

  return {
    installs: [],
    printScales: options.printScales,
    rooms: getRugRooms(state.rooms, item.rug),
    brands: options.brands,
    products: filterRugProducts(_values(state.trykLayouts.itemsById)).map(x => getTrykProductOption(x))
  };
}

function getRugRooms(state: RoomsState, rug: TrykApi.Catalog.IRug): RoomSceneOption[] {
  if (!state || !rug) {
    return [];
  }

  const rugRooms = getValidRugRooms(state);

  return rugRooms.map(
    x => ({
      ...x,
      thumbnailUrl: ImagesClient.Urls.Thumbnail.room(x.roomId)
    })
  );
}

function getBasketRugDisplay(input: TrykApi.Catalog.IRug): BasketItemDisplay {
  const getThumbnailUrl = (rug: TrykApi.Catalog.IRug): string => {
    return ImagesClient.Urls.Thumbnail.rug({
      designs: rug.components.map(x => x.designCode),
      sections: rug.components.map(x => x.sectionId),
      pal: rug.palette.map(x => x.code),
      pos: rug.components.map(x => x.positions),
      scales: rug.components.map(x => x.scale),
      texture: rug.textureId,
      pixels: 100
    });
  };

  return {
    primaryDisplay: 'Custom Rug',
    secondaryDisplay: input.components.length > 1 ? 'Multiple Designs' : '',
    thumbnailUrl: getThumbnailUrl(input)
  };
}

function getTrykProductOption(item: TrykApi.Catalog.ITrykProduct): TrykProductOption {
  return {
    ...item,
    thumbnailUrl: ImagesClient.Urls.Thumbnail.trykProduct(item.productId),
    hasSwatch: !!item.frames.find(x => x.frameContentTypeId === 1 || x.frameContentTypeId === 4),
    hasRoom: !!item.frames.find(x => x.frameContentTypeId === 2)
  };
}
